// A GitHub label's colour, made legible. A label's colour is its owner's
// choice (any of 16 million), so the text on it is worked out, and in the
// dark theme the colour itself is: the one place a label's colours come
// from, for every chip in the app.
//
// - Light: the label's own colour as the chip's background, with black or
//   white text, whichever contrasts more (always at least 4.5:1). A pale
//   label gets an edge, so a white label is still a chip on a white row.
// - Dark: as GitHub does, not a saturated block: a translucent tint of the
//   colour as background, and the colour lightened (its hue kept) as text
//   and edge, lightened further until it reads at 4.5:1 on the tint over
//   every dark surface a chip sits on.
//
// The dark surfaces are the theme's own (styles/tokens.css). They are
// written out here, since a stylesheet cannot be read where this runs, and
// labelColor.test.ts holds them to the stylesheet as the theme's own test
// reads it (styles/contrast.ts): a token that changes fails that test until
// DARK_SURFACES says the same, and the contrast tests then run against the
// new surfaces. Contrast is that module's WCAG ratio, the theme's measure.
import { luminance as luminanceOf, wcag } from '../../styles/contrast';

/** Red, green, blue (0–255) and alpha (0–1). */
export type Rgba = readonly [number, number, number, number];

/** A chip's colours in one theme, as CSS colours. */
export interface Tone {
  bg: string;
  fg: string;
  border: string;
}

/** The dark theme's tokens a chip sits on: a card, a well (a stack's title bar), the page, a hovered row. */
export const DARK_SURFACE_TOKENS = ['surface', 'code', 'bg', 'soft'] as const;

/** Those surfaces' colours in the dark theme, in that order; held to tokens.css by labelColor.test.ts. */
export const DARK_SURFACES: readonly string[] = ['#272d27', '#1f2520', '#1b201d', '#413228'];

const DEFAULT = 'ededed';
const TINT = 0.18;
const AA = 4.5;

/** A CSS colour as written here (`#rrggbb` or `rgba(r, g, b, a)`). */
export function parseColor(css: string): Rgba {
  const hex = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(css.trim());
  if (hex) return [parseInt(hex[1], 16), parseInt(hex[2], 16), parseInt(hex[3], 16), 1];
  const fn = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/.exec(css.trim());
  if (fn) return [Number(fn[1]), Number(fn[2]), Number(fn[3]), fn[4] === undefined ? 1 : Number(fn[4])];
  return parseColor(`#${DEFAULT}`);
}

/** `top` laid over the opaque `under`. */
export function over(top: Rgba, under: Rgba): Rgba {
  const mix = (i: 0 | 1 | 2) => Math.round(top[i] * top[3] + under[i] * (1 - top[3]));
  return [mix(0), mix(1), mix(2), 1];
}


/** An opaque colour as `#rrggbb`. */
export const hex = ([r, g, b]: Rgba) => `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;

function toHsl([r, g, b]: Rgba): [number, number, number] {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === rn ? (gn - bn) / d + (gn < bn ? 6 : 0) : max === gn ? (bn - rn) / d + 2 : (rn - gn) / d + 4;
  return [h / 6, s, l];
}

function fromHsl(h: number, s: number, l: number): Rgba {
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const ch = (t: number) => {
    const x = ((t % 1) + 1) % 1;
    const v = x < 1 / 6 ? p + (q - p) * 6 * x : x < 1 / 2 ? q : x < 2 / 3 ? p + (q - p) * (2 / 3 - x) * 6 : p;
    return Math.round(v * 255);
  };
  return [ch(h + 1 / 3), ch(h), ch(h - 1 / 3), 1];
}

function light(c: Rgba): Tone {
  const bg = hex(c);
  return {
    bg,
    fg: wcag('#000000', bg) >= wcag('#ffffff', bg) ? '#000000' : '#ffffff',
    // Pale enough to be lost on a light row: an edge of ink.
    border: luminanceOf(bg) > 0.6 ? 'rgba(0, 0, 0, 0.22)' : bg,
  };
}

function dark(c: Rgba): Tone {
  const [h, s] = toHsl(c);
  // The hue kept, the saturation eased (a pure primary glares on dark), and as light as it must be to read.
  for (let l = Math.max(toHsl(c)[2], 0.62); ; l = Math.min(1, l + 0.03)) {
    const fg = fromHsl(h, Math.min(s, 0.85), l);
    const bg: Rgba = [fg[0], fg[1], fg[2], TINT];
    if (l >= 1 || DARK_SURFACES.every((surface) => wcag(hex(fg), hex(over(bg, parseColor(surface)))) >= AA))
      return { bg: `rgba(${fg[0]}, ${fg[1]}, ${fg[2]}, ${TINT})`, fg: hex(fg), border: `rgba(${fg[0]}, ${fg[1]}, ${fg[2]}, 0.4)` };
  }
}

/** A label's chip colours in each theme, from its colour (six hex digits; anything else is GitHub's default grey). */
export function labelTones(color: string): { light: Tone; dark: Tone } {
  const c = parseColor(/^#?[0-9a-f]{6}$/i.test(color.trim()) ? color : DEFAULT);
  return { light: light(c), dark: dark(c) };
}
